import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { Button, Drawer, Form, Space } from 'antd';
import { routePaths } from 'router';
import { useCreateTrip } from 'api/trips/use-create-trip.ts';
import { useSelectCargoStore } from 'shared/stores/use-select-cargos.ts';
import { formatDate } from 'shared/utils/time/format-date';
import type { ITripFormFields } from 'components/trips/trip-form';
import { TripForm } from 'components/trips/trip-form';
import type { TCreateTripReq } from '../../../../../api/swagger';

interface ICargoSelectCreateTripProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

export const CargoSelectCreateTrip: React.FC<ICargoSelectCreateTripProps> = (props) => {
  const { open, setOpen } = props;
  const formId = useId();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedCargos, selectedCargoItems, deleteAll } = useSelectCargoStore();

  const { mutateAsync: createTrip, isLoading } = useCreateTrip();

  const [form] = Form.useForm();

  const onClose = () => {
    setOpen(false);
    form.resetFields();
  };

  const onFinish = async (values: ITripFormFields) => {
    const apiVariables: TCreateTripReq = {
      currency: values.currency,
      soldToPartners: values.soldToPartners,
      costPrice: values.costPrice,
      estimatedStartDate: values.estimatedDate[0] && formatDate(values.estimatedDate[0]),
      estimatedEndDate: values.estimatedDate[1] && formatDate(values.estimatedDate[1]),
      ...(values.soldToPartners
        ? {
            carrierId: values.carrier?.value,
            contactId: values.contact?.value,
          }
        : {
            truckId: values.truckId,
            trailerId: values.trailerId,
            trailerPlate: !values.trailerId ? values.trailerPlate : undefined,
          }),
      cargoIds: Array.from(selectedCargos.keys()),
      cargoItemIds: Array.from(selectedCargoItems.keys()),
    };

    const result = await createTrip(apiVariables);
    onClose();
    deleteAll();

    navigate(generatePath(routePaths.trip, { tripId: String(result.id) }));
  };

  return (
    <Drawer
      title={t('trip:create_trip')}
      placement='right'
      open={open}
      extra={
        <Space>
          <Button form={formId} htmlType='submit' type='primary' loading={isLoading}>
            {t('common:save')}
          </Button>
        </Space>
      }
      onClose={onClose}>
      {open ? <TripForm form={form} formId={formId} onFinish={onFinish} /> : null}
    </Drawer>
  );
};
